import dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import {
  DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { cloneListOfCloneable, isSameData, isSameList } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { SeatInfo } from './seat-info.js';
import { TimeZone } from '../../../time/timezone.js';
import { TrueTime } from '../../../time/true-time.js';
import { SeatInfoSerializer } from '../../../serialization/seat-info-serializer.js';
import { SeatingStateBase } from './seating-state-base.js';

/**
 * Use {@link SeatingStateHelper} to mutate instances of this class.
 */
export class PassSeatingState extends SeatingStateBase {
  static readonly TYPE_ID: string = 'f795d4cf24724be0b8600661712e7765';

  constructor(
    public readonly seats: SeatInfo[],
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly passTemplateId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return PassSeatingState.TYPE_ID;
  }

  static empty({
    businessRef,
    passTemplateId,
  }: {
    businessRef: BusinessModuleRef,
    passTemplateId: string
  }): PassSeatingState {
    return new PassSeatingState(
        [],
        businessRef,
        TrueTime.now(),
        passTemplateId,
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): PassSeatingState {
    return new PassSeatingState(
        SeatInfoSerializer.deserialize(context.fromBinaryBlob(MapUtils.getRaw(map, 'seats'))),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<string>(map, 'passTemplateId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      seatCount: this.seatCount,
      seats: context.toBinaryBlob(SeatInfoSerializer.serialize(this.seats)),
      businessRef: this.businessRef.encode(),
      creationDate: context.encodeDate(this.creationDate),
      passTemplateId: this.passTemplateId,
    };
  }

  clone(): this {
    return new PassSeatingState(
        cloneListOfCloneable(this.seats),
        this.businessRef.clone(),
        TimeZone.cloneDate(this.creationDate),
        this.passTemplateId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.seatCount === this.seatCount &&
        isSameList(other.seats, this.seats, (a, b) => isSameData(a, b)) &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.passTemplateId === this.passTemplateId;
  }
}

export class PassSeatingStateBuilder extends DataModelBuilder<PassSeatingState> {
  build(context: SerializationContext, map: SerializedData): PassSeatingState {
    return PassSeatingState.fromMap(context, map);
  }
}
